'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createSocialPost, deleteSocialPost } from '@/src/actions/social-posts';
import type { ISocialPost } from 'types/src/types';

type CreatePostPayload = {
  textHtml: string;
  textPlain: string;
  gifId?: string | null;
  uploadedImagesUrl?: string[];
  uploadedVideoUrl?: string;
  youTubeVideoUrl?: string;
  projectId?: number | null;
  igdbGameMeta: any;
  totalCost: number;
};

function removePostFromInfinite(data: any, postId: number) {
  if (!data?.pages) return data;

  return {
    ...data,
    pages: data.pages.map((page: any) => ({
      ...page,
      posts: (page.posts ?? []).filter(
        (p: any) => Number(p.id) !== Number(postId)
      ),
    })),
  };
}

const FEED_ROOT_KEYS = [
  'discoverPosts',
  'followedPosts',
  'profilePosts',
] as const;

function isFeedQueryKey(key: unknown): key is [string, ...unknown[]] {
  return (
    Array.isArray(key) &&
    typeof key[0] === 'string' &&
    (FEED_ROOT_KEYS as readonly string[]).includes(key[0])
  );
}

export function useCreatePostMutation() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreatePostPayload) => createSocialPost(payload),

    onSuccess: (res) => {
      if (!res?.data) return;

      const newPost = res.data as ISocialPost;

      qc.setQueriesData(
        {
          predicate: (q) =>
            isFeedQueryKey(q.queryKey) && q.queryKey[0] === 'discoverPosts',
        },
        (data: any) => {
          if (!data?.pages?.length) {
            return { pages: [{ posts: [newPost] }], pageParams: [1] };
          }
          return {
            ...data,
            pages: data.pages.map((page: any, idx: number) =>
              idx === 0
                ? { ...page, posts: [newPost, ...(page.posts ?? [])] }
                : page
            ),
          };
        }
      );

      qc.invalidateQueries({
        predicate: (q) =>
          isFeedQueryKey(q.queryKey) && q.queryKey[0] === 'discoverPosts',
      });
    },
  });
}

export function useDeletePostMutation() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (postId: number) => deleteSocialPost(postId),

    onMutate: async (postId) => {
      const queries = qc.getQueryCache().findAll({
        predicate: (q) => isFeedQueryKey(q.queryKey),
      });

      const snapshots = queries.map((q) => ({
        key: q.queryKey,
        data: qc.getQueryData(q.queryKey),
      }));

      await Promise.all(
        queries.map((q) => qc.cancelQueries({ queryKey: q.queryKey }))
      );

      queries.forEach((q) => {
        qc.setQueryData(q.queryKey, (data: any) =>
          removePostFromInfinite(data, postId)
        );
      });

      return { snapshots };
    },

    onError: (_err, _postId, ctx) => {
      ctx?.snapshots?.forEach(({ key, data }) => {
        qc.setQueryData(key, data);
      });
    },

    onSettled: () => {
      qc.invalidateQueries({
        predicate: (q) => isFeedQueryKey(q.queryKey),
      });
    },
  });
}
